import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/layout/AdminLayout';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
    TextInput,
    Alert,
    ActivityIndicator,
    Modal,
    Platform,
    Image
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { getApiUrl } from '../../config/development';
import AsyncStorage from '@react-native-async-storage/async-storage';

const MentorManagementScreen = ({ navigation }) => {
    const [mentors, setMentors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingMentor, setEditingMentor] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        phone: '',
        mentorType: '',
        mentorBio: '',
        instagram: '',
        twitter: '',
        linkedin: '',
        profileImage: null
    });

    useEffect(() => {
        fetchMentors();
    }, []);

    const fetchMentors = async () => {
        try {
            const token = await AsyncStorage.getItem('adminAuthToken') || await AsyncStorage.getItem('token');
            const response = await fetch(`${getApiUrl()}/admin/mentors`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const result = await response.json();
            if (result.success) {
                setMentors(result.data);
            }
        } catch (error) {
            console.error('Error fetching mentors:', error);
            Alert.alert('Error', 'Failed to load mentors');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateMentor = async () => {
        if (!formData.name || !formData.email || !formData.password) {
            Alert.alert('Error', 'Please fill in all required fields');
            return;
        }

        try {
            const token = await AsyncStorage.getItem('adminAuthToken') || await AsyncStorage.getItem('token');
            const data = new FormData();

            data.append('name', formData.name);
            data.append('email', formData.email);
            data.append('password', formData.password);
            data.append('phone', formData.phone);
            data.append('mentorType', formData.mentorType);
            data.append('mentorBio', formData.mentorBio);

            const socialLinks = {};
            if (formData.instagram) socialLinks.instagram = formData.instagram;
            if (formData.twitter) socialLinks.twitter = formData.twitter;
            if (formData.linkedin) socialLinks.linkedin = formData.linkedin;
            data.append('socialLinks', JSON.stringify(socialLinks));

            if (formData.profileImage && formData.profileImage.uri) {
                const imageUri = formData.profileImage.uri;
                const filename = imageUri.split('/').pop();

                if (Platform.OS === 'web') {
                    try {
                        const response = await fetch(imageUri);
                        const blob = await response.blob();
                        data.append('profileImage', blob, filename || 'profile.jpg');
                    } catch (e) {
                        console.error('Failed to convert uri to blob:', e);
                    }
                } else {
                    const match = /\.(\w+)$/.exec(filename);
                    const type = match ? `image/${match[1]}` : `image`;
                    data.append('profileImage', {
                        uri: Platform.OS === 'ios' ? imageUri.replace('file://', '') : imageUri,
                        name: filename || 'profile.jpg',
                        type: type
                    });
                }
            }

            // Debug: Log FormData entries
            if (Platform.OS === 'web') {
                console.log('Sending FormData:');
                for (let pair of data.entries()) {
                    console.log(pair[0] + ': ' + (pair[1] instanceof Blob ? `Blob (${pair[1].type})` : pair[1]));
                }
            }

            const response = await fetch(`${getApiUrl()}/admin/create-mentor`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: data
            });

            const result = await response.json();
            if (result.success) {
                Alert.alert('Success', 'Mentor created successfully');
                setShowCreateModal(false);
                resetForm();
                fetchMentors();
            } else {
                Alert.alert('Error', result.error || 'Failed to create mentor');
            }
        } catch (error) {
            console.error('Error creating mentor:', error);
            Alert.alert('Error', 'Failed to create mentor');
        }
    };

    const handleUpdateMentor = async (mentorId) => {
        try {
            const token = await AsyncStorage.getItem('adminAuthToken') || await AsyncStorage.getItem('token');
            const data = new FormData();

            data.append('isMentor', 'true');
            data.append('mentorType', formData.mentorType);
            data.append('mentorBio', formData.mentorBio);

            const socialLinks = {};
            if (formData.instagram) socialLinks.instagram = formData.instagram;
            if (formData.twitter) socialLinks.twitter = formData.twitter;
            if (formData.linkedin) socialLinks.linkedin = formData.linkedin;
            data.append('socialLinks', JSON.stringify(socialLinks));

            // Only append image if it's a new selection (has uri and doesn't look like a URL)
            if (formData.profileImage && formData.profileImage.uri && !formData.profileImage.uri.startsWith('http')) {
                const imageUri = formData.profileImage.uri;
                const filename = imageUri.split('/').pop();

                if (Platform.OS === 'web') {
                    try {
                        const response = await fetch(imageUri);
                        const blob = await response.blob();
                        data.append('profileImage', blob, filename || 'profile.jpg');
                    } catch (e) {
                        console.error('Failed to convert uri to blob:', e);
                    }
                } else {
                    const match = /\.(\w+)$/.exec(filename);
                    const type = match ? `image/${match[1]}` : `image`;
                    data.append('profileImage', {
                        uri: Platform.OS === 'ios' ? imageUri.replace('file://', '') : imageUri,
                        name: filename || 'profile.jpg',
                        type: type
                    });
                }
            }

            // Debug: Log FormData entries
            if (Platform.OS === 'web') {
                console.log('Updating Mentor - FormData:');
                for (let pair of data.entries()) {
                    console.log(pair[0] + ': ' + (pair[1] instanceof Blob ? `Blob (${pair[1].type})` : pair[1]));
                }
            }

            const response = await fetch(`${getApiUrl()}/admin/users/${mentorId}/mentor`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: data
            });

            const result = await response.json();
            if (result.success) {
                Alert.alert('Success', 'Mentor updated successfully');
                setEditingMentor(null);
                resetForm();
                fetchMentors();
            } else {
                Alert.alert('Error', result.error || 'Failed to update mentor');
            }
        } catch (error) {
            console.error('Error updating mentor:', error);
            Alert.alert('Error', 'Failed to update mentor');
        }
    };

    const handleRemoveMentor = async (mentorId) => {
        Alert.alert(
            'Confirm',
            'Remove mentor status from this user?',
            [
                { text: 'Cancel', style: 'cancel' },
                {
                    text: 'Remove',
                    style: 'destructive',
                    onPress: async () => {
                        try {
                            const token = await AsyncStorage.getItem('adminAuthToken') || await AsyncStorage.getItem('token');
                            const response = await fetch(`${getApiUrl()}/admin/users/${mentorId}/mentor`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                },
                                body: JSON.stringify({
                                    isMentor: false,
                                    role: 'youth'
                                })
                            });

                            const result = await response.json();
                            if (result.success) {
                                Alert.alert('Success', 'Mentor status removed');
                                fetchMentors();
                            }
                        } catch (error) {
                            Alert.alert('Error', 'Failed to remove mentor status');
                        }
                    }
                }
            ]
        );
    };

    const handleToggleStatus = async (mentorId, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

        try {
            const token = await AsyncStorage.getItem('adminAuthToken') || await AsyncStorage.getItem('token');
            const response = await fetch(`${getApiUrl()}/admin/users/${mentorId}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ status: newStatus })
            });

            const result = await response.json();
            if (result.success) {
                Alert.alert('Success', `Mentor ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
                fetchMentors();
            } else {
                Alert.alert('Error', result.error || 'Failed to update status');
            }
        } catch (error) {
            console.error('Toggle status error:', error);
            Alert.alert('Error', 'Failed to toggle mentor status');
        }
    };

    const handlePickImage = async () => {
        try {
            const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
            if (status !== 'granted') {
                Alert.alert('Permission Denied', 'We need camera roll permissions to upload a photo.');
                return;
            }

            const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ImagePicker.MediaTypeOptions.Images,
                allowsEditing: true,
                aspect: [1, 1],
                quality: 0.7,
            });

            if (!result.canceled && result.assets[0]) {
                setFormData(prev => ({ ...prev, profileImage: result.assets[0] }));
            }
        } catch (error) {
            console.error('Pick image error:', error);
            Alert.alert('Error', 'Failed to pick image');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            password: '',
            phone: '',
            mentorType: '',
            mentorBio: '',
            instagram: '',
            twitter: '',
            linkedin: '',
            profileImage: null
        });
    };

    const openEditModal = (mentor) => {
        setEditingMentor(mentor);
        setFormData({
            name: mentor.name,
            email: mentor.email,
            password: '',
            phone: mentor.phone || '',
            mentorType: mentor.mentorType || '',
            mentorBio: mentor.mentorBio || '',
            instagram: mentor.socialLinks?.instagram || '',
            twitter: mentor.socialLinks?.twitter || '',
            linkedin: mentor.socialLinks?.linkedin || '',
            profileImage: mentor.profileImageUrl ? { uri: mentor.profileImageUrl } : null
        });
        setShowCreateModal(true);
    };

    if (loading) {
        return (
            <AdminLayout>
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color="#6366F1" />
                </View>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <View style={styles.container}>
                {Platform.OS !== 'web' && (
                    <View style={styles.header}>
                        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
                            <Ionicons name="arrow-back" size={24} color="#111827" />
                        </TouchableOpacity>
                        <Text style={styles.headerTitle}>Mentor Management</Text>
                        <TouchableOpacity
                            style={styles.addButton}
                            onPress={() => {
                                resetForm();
                                setEditingMentor(null);
                                setShowCreateModal(true);
                            }}
                        >
                            <Ionicons name="add-circle" size={28} color="#6366F1" />
                        </TouchableOpacity>
                    </View>
                )}

                <ScrollView style={styles.content}>
                    <View style={styles.pageHeader}>
                        <Text style={styles.pageTitle}>Mentor Management</Text>
                        <TouchableOpacity
                            style={styles.createButton}
                            onPress={() => {
                                resetForm();
                                setEditingMentor(null);
                                setShowCreateModal(true);
                            }}
                        >
                            <Ionicons name="add-circle" size={20} color="#FFFFFF" />
                            <Text style={styles.createButtonText}>Create Mentor</Text>
                        </TouchableOpacity>
                    </View>

                    <View style={styles.statsCard}>
                        <Text style={styles.statsNumber}>{mentors.length}</Text>
                        <Text style={styles.statsLabel}>Active Mentors & Coaches</Text>
                    </View>

                    {mentors.map((mentor) => (
                        <View key={mentor.id} style={styles.mentorCard}>
                            <View style={styles.mentorHeader}>
                                <Image
                                    source={{ uri: mentor.profileImageUrl || 'https://i.pravatar.cc/150' }}
                                    style={styles.mentorAvatar}
                                />
                                <View style={styles.mentorInfo}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                        <Text style={styles.mentorName}>{mentor.name}</Text>
                                        <View style={[
                                            styles.statusBadge,
                                            { backgroundColor: mentor.status === 'active' ? '#DEF7EC' : '#FDE8E8' }
                                        ]}>
                                            <Text style={[
                                                styles.statusText,
                                                { color: mentor.status === 'active' ? '#03543F' : '#9B1C1C' }
                                            ]}>
                                                {mentor.status ? (mentor.status.charAt(0).toUpperCase() + mentor.status.slice(1)) : 'Active'}
                                            </Text>
                                        </View>
                                    </View>
                                    <Text style={styles.mentorType}>{mentor.mentorType || 'Mentor'}</Text>
                                    <Text style={styles.mentorEmail}>{mentor.email}</Text>
                                </View>
                                <View style={styles.mentorActions}>
                                    <TouchableOpacity
                                        style={styles.actionButton}
                                        onPress={() => handleToggleStatus(mentor.id, mentor.status)}
                                    >
                                        <Ionicons
                                            name={mentor.status === 'active' ? "pause-circle-outline" : "play-circle-outline"}
                                            size={20}
                                            color={mentor.status === 'active' ? "#F59E0B" : "#10B981"}
                                        />
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                        style={styles.actionButton}
                                        onPress={() => openEditModal(mentor)}
                                    >
                                        <Ionicons name="create-outline" size={20} color="#6366F1" />
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                        style={styles.actionButton}
                                        onPress={() => handleRemoveMentor(mentor.id)}
                                    >
                                        <Ionicons name="trash-outline" size={20} color="#EF4444" />
                                    </TouchableOpacity>
                                </View>
                            </View>

                            {mentor.mentorBio && (
                                <Text style={styles.mentorBio} numberOfLines={2}>{mentor.mentorBio}</Text>
                            )}

                            {mentor.socialLinks && (
                                <View style={styles.socialLinks}>
                                    {mentor.socialLinks.instagram && (
                                        <Ionicons name="logo-instagram" size={16} color="#9CA3AF" />
                                    )}
                                    {mentor.socialLinks.twitter && (
                                        <Ionicons name="logo-twitter" size={16} color="#9CA3AF" />
                                    )}
                                    {mentor.socialLinks.linkedin && (
                                        <Ionicons name="logo-linkedin" size={16} color="#9CA3AF" />
                                    )}
                                </View>
                            )}
                        </View>
                    ))}
                </ScrollView>

                <Modal
                    visible={showCreateModal}
                    animationType="slide"
                    transparent={true}
                    onRequestClose={() => setShowCreateModal(false)}
                >
                    <View style={styles.modalOverlay}>
                        <View style={styles.modalContent}>
                            <View style={styles.modalHeader}>
                                <Text style={styles.modalTitle}>
                                    {editingMentor ? 'Edit Mentor' : 'Create New Mentor'}
                                </Text>
                                <TouchableOpacity onPress={() => setShowCreateModal(false)}>
                                    <Ionicons name="close" size={24} color="#6B7280" />
                                </TouchableOpacity>
                            </View>

                            <ScrollView style={styles.modalForm}>
                                <View style={styles.imagePickerContainer}>
                                    <TouchableOpacity style={styles.imagePicker} onPress={handlePickImage}>
                                        {formData.profileImage ? (
                                            <Image source={{ uri: formData.profileImage.uri }} style={styles.pickedImage} />
                                        ) : (
                                            <View style={styles.imagePlaceholder}>
                                                <Ionicons name="camera" size={32} color="#9CA3AF" />
                                                <Text style={styles.imagePlaceholderText}>Upload Photo</Text>
                                            </View>
                                        )}
                                    </TouchableOpacity>
                                    {formData.profileImage && (
                                        <TouchableOpacity
                                            style={styles.removeImageButton}
                                            onPress={() => setFormData(prev => ({ ...prev, profileImage: null }))}
                                        >
                                            <Text style={styles.removeImageText}>Remove</Text>
                                        </TouchableOpacity>
                                    )}
                                </View>

                                <Text style={styles.inputLabel}>Name *</Text>
                                <TextInput
                                    style={styles.input}
                                    value={formData.name}
                                    onChangeText={(text) => setFormData({ ...formData, name: text })}
                                    placeholder="Full Name"
                                    editable={!editingMentor}
                                />

                                <Text style={styles.inputLabel}>Email *</Text>
                                <TextInput
                                    style={styles.input}
                                    value={formData.email}
                                    onChangeText={(text) => setFormData({ ...formData, email: text })}
                                    placeholder="email@example.com"
                                    keyboardType="email-address"
                                    autoCapitalize="none"
                                    editable={!editingMentor}
                                />

                                {!editingMentor && (
                                    <>
                                        <Text style={styles.inputLabel}>Password *</Text>
                                        <TextInput
                                            style={styles.input}
                                            value={formData.password}
                                            onChangeText={(text) => setFormData({ ...formData, password: text })}
                                            placeholder="Password"
                                            secureTextEntry
                                        />
                                    </>
                                )}

                                <Text style={styles.inputLabel}>Phone</Text>
                                <TextInput
                                    style={styles.input}
                                    value={formData.phone}
                                    onChangeText={(text) => setFormData({ ...formData, phone: text })}
                                    placeholder="+254..."
                                    keyboardType="phone-pad"
                                />

                                <Text style={styles.inputLabel}>Mentor Type</Text>
                                <TextInput
                                    style={styles.input}
                                    value={formData.mentorType}
                                    onChangeText={(text) => setFormData({ ...formData, mentorType: text })}
                                    placeholder="e.g., Business Coach, Tech Mentor"
                                />

                                <Text style={styles.inputLabel}>Bio</Text>
                                <TextInput
                                    style={[styles.input, styles.textArea]}
                                    value={formData.mentorBio}
                                    onChangeText={(text) => setFormData({ ...formData, mentorBio: text })}
                                    placeholder="Brief bio about the mentor..."
                                    multiline
                                    numberOfLines={4}
                                />

                                <Text style={styles.sectionTitle}>Social Links</Text>

                                <Text style={styles.inputLabel}>Instagram</Text>
                                <TextInput
                                    style={styles.input}
                                    value={formData.instagram}
                                    onChangeText={(text) => setFormData({ ...formData, instagram: text })}
                                    placeholder="https://instagram.com/username"
                                    autoCapitalize="none"
                                />

                                <Text style={styles.inputLabel}>Twitter</Text>
                                <TextInput
                                    style={styles.input}
                                    value={formData.twitter}
                                    onChangeText={(text) => setFormData({ ...formData, twitter: text })}
                                    placeholder="https://twitter.com/username"
                                    autoCapitalize="none"
                                />

                                <Text style={styles.inputLabel}>LinkedIn</Text>
                                <TextInput
                                    style={styles.input}
                                    value={formData.linkedin}
                                    onChangeText={(text) => setFormData({ ...formData, linkedin: text })}
                                    placeholder="https://linkedin.com/in/username"
                                    autoCapitalize="none"
                                />

                                <TouchableOpacity
                                    style={styles.submitButton}
                                    onPress={() => editingMentor ? handleUpdateMentor(editingMentor.id) : handleCreateMentor()}
                                >
                                    <Text style={styles.submitButtonText}>
                                        {editingMentor ? 'Update Mentor' : 'Create Mentor'}
                                    </Text>
                                </TouchableOpacity>
                            </ScrollView>
                        </View>
                    </View>
                </Modal>
            </View>
        </AdminLayout>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F9FAFB',
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 20,
        backgroundColor: '#FFFFFF',
        borderBottomWidth: 1,
        borderBottomColor: '#E5E7EB',
    },
    backButton: {
        padding: 8,
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: '#111827',
    },
    addButton: {
        padding: 8,
    },
    content: {
        flex: 1,
        padding: 20,
    },
    pageHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
    },
    pageTitle: {
        fontSize: 24,
        fontWeight: '700',
        color: '#111827',
    },
    createButton: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#6366F1',
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 8,
        gap: 8,
    },
    createButtonText: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '600',
    },
    statsCard: {
        backgroundColor: '#6366F1',
        borderRadius: 16,
        padding: 24,
        marginBottom: 20,
        alignItems: 'center',
    },
    statsNumber: {
        fontSize: 48,
        fontWeight: '800',
        color: '#FFFFFF',
    },
    statsLabel: {
        fontSize: 14,
        color: 'rgba(255,255,255,0.9)',
        marginTop: 4,
    },
    mentorCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: 12,
        padding: 16,
        marginBottom: 12,
        borderWidth: 1,
        borderColor: '#E5E7EB',
    },
    mentorHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
    },
    mentorAvatar: {
        width: 60,
        height: 60,
        borderRadius: 30,
        backgroundColor: '#F3F4F6',
    },
    mentorInfo: {
        flex: 1,
    },
    mentorName: {
        fontSize: 16,
        fontWeight: '700',
        color: '#111827',
        marginBottom: 4,
    },
    mentorType: {
        fontSize: 14,
        color: '#6366F1',
        fontWeight: '600',
        marginBottom: 4,
    },
    mentorEmail: {
        fontSize: 12,
        color: '#6B7280',
    },
    mentorActions: {
        flexDirection: 'row',
        gap: 8,
    },
    actionButton: {
        padding: 8,
    },
    mentorBio: {
        fontSize: 13,
        color: '#4B5563',
        marginTop: 12,
        lineHeight: 18,
    },
    socialLinks: {
        flexDirection: 'row',
        gap: 12,
        marginTop: 12,
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'flex-end',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        maxHeight: '90%',
    },
    modalHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 20,
        borderBottomWidth: 1,
        borderBottomColor: '#E5E7EB',
    },
    modalTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: '#111827',
    },
    modalForm: {
        padding: 20,
    },
    imagePickerContainer: {
        alignItems: 'center',
        marginBottom: 24,
    },
    imagePicker: {
        width: 120,
        height: 120,
        borderRadius: 60,
        backgroundColor: '#F3F4F6',
        borderWidth: 1,
        borderColor: '#E5E7EB',
        borderStyle: 'dashed',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
    },
    pickedImage: {
        width: '100%',
        height: '100%',
    },
    imagePlaceholder: {
        alignItems: 'center',
    },
    imagePlaceholderText: {
        fontSize: 12,
        color: '#9CA3AF',
        marginTop: 4,
    },
    removeImageButton: {
        marginTop: 8,
    },
    removeImageText: {
        fontSize: 14,
        color: '#EF4444',
        fontWeight: '500',
    },
    inputLabel: {
        fontSize: 14,
        fontWeight: '600',
        color: '#374151',
        marginBottom: 8,
        marginTop: 12,
    },
    input: {
        borderWidth: 1,
        borderColor: '#D1D5DB',
        borderRadius: 8,
        padding: 12,
        fontSize: 14,
        color: '#111827',
        backgroundColor: '#FFFFFF',
    },
    textArea: {
        height: 100,
        textAlignVertical: 'top',
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: '#111827',
        marginTop: 24,
        marginBottom: 8,
    },
    submitButton: {
        backgroundColor: '#6366F1',
        borderRadius: 12,
        padding: 16,
        alignItems: 'center',
        marginTop: 24,
        marginBottom: 40,
    },
    submitButtonText: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: '700',
    },
    statusBadge: {
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 12,
    },
    statusText: {
        fontSize: 10,
        fontWeight: '700',
    },
});

export default MentorManagementScreen;
